import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/useAuth";
import { zodResolver } from "@hookform/resolvers/zod";
import { CheckCircle2, KeyRound, Mail } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Navigate, useSearchParams } from "react-router-dom";
import { z } from "zod";

const magicSchema = z.object({
  email: z.string().email("Enter a valid email address."),
});
type MagicValues = z.infer<typeof magicSchema>;

const passwordSchema = z.object({
  email: z.string().email("Enter a valid email address."),
  password: z.string().min(1, "Enter your password."),
});
type PasswordValues = z.infer<typeof passwordSchema>;

/**
 * Treat the `?from=` parameter as untrusted. Only allow same-origin paths
 * starting with a single `/`. Anything else (full URLs, protocol-relative
 * paths like `//evil.com/x`, javascript:, etc.) falls back to the dashboard.
 */
function safeRedirect(raw: string | null): string {
  if (!raw) return "/";
  if (!raw.startsWith("/")) return "/";
  if (raw.startsWith("//")) return "/";
  return raw;
}

type Mode = "magic" | "password";

export function Login() {
  const { user, signInWithEmail, signInWithPassword, loading } = useAuth();
  const [params] = useSearchParams();
  const [mode, setMode] = useState<Mode>("magic");
  const [sent, setSent] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);

  const magicForm = useForm<MagicValues>({ resolver: zodResolver(magicSchema) });
  const passwordForm = useForm<PasswordValues>({ resolver: zodResolver(passwordSchema) });

  useEffect(() => {
    setSent(false);
    setGlobalError(null);
  }, []);

  if (loading) return null;
  if (user) {
    return <Navigate to={safeRedirect(params.get("from"))} replace />;
  }

  const redirectTo = `${window.location.origin}/auth/callback`;

  const onMagicSubmit = magicForm.handleSubmit(async ({ email }) => {
    setGlobalError(null);
    const { error } = await signInWithEmail(email, redirectTo);
    if (error) {
      setGlobalError("We could not send a sign-in link. Please try again or contact us.");
      return;
    }
    setSent(true);
  });

  const onPasswordSubmit = passwordForm.handleSubmit(async ({ email, password }) => {
    setGlobalError(null);
    const { error } = await signInWithPassword(email, password);
    if (error) {
      setGlobalError("Wrong email or password.");
    }
    // On success, AuthProvider's onAuthStateChange flips user → Navigate above redirects.
  });

  return (
    <div className="min-h-dvh flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-8">
          <BrandLogo size={40} />
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Tenant Login</CardTitle>
            <CardDescription>Sign in to your Trinium tenant portal.</CardDescription>
          </CardHeader>
          <CardContent>
            {sent ? (
              <div
                role="status"
                className="flex items-start gap-3 rounded-xl border border-line bg-white/[0.02] p-4"
              >
                <CheckCircle2 className="text-accent mt-0.5" size={18} />
                <div className="text-sm text-ink-dim leading-relaxed">
                  Check your email. If your address is registered with Trinium, a sign-in link has
                  been sent. The link expires in a few minutes.
                </div>
              </div>
            ) : mode === "magic" ? (
              <form className="flex flex-col gap-4" onSubmit={onMagicSubmit} noValidate>
                <Field
                  id="email-magic"
                  label="Email"
                  error={magicForm.formState.errors.email?.message}
                >
                  <Input
                    id="email-magic"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    disabled={magicForm.formState.isSubmitting}
                    {...magicForm.register("email")}
                  />
                </Field>
                {globalError ? (
                  <p role="alert" className="text-xs text-red-300">
                    {globalError}
                  </p>
                ) : null}
                <Button type="submit" disabled={magicForm.formState.isSubmitting} className="mt-2">
                  <Mail size={16} />
                  {magicForm.formState.isSubmitting ? "Sending…" : "Send sign-in link"}
                </Button>
                <button
                  type="button"
                  className="text-xs text-ink-dim hover:text-ink underline-offset-2 hover:underline"
                  onClick={() => {
                    setGlobalError(null);
                    setMode("password");
                  }}
                >
                  Use password instead
                </button>
                <p className="text-xs text-ink-dim text-center">
                  Access is by invitation. No public signups.
                </p>
              </form>
            ) : (
              <form className="flex flex-col gap-4" onSubmit={onPasswordSubmit} noValidate>
                <Field
                  id="email-pw"
                  label="Email"
                  error={passwordForm.formState.errors.email?.message}
                >
                  <Input
                    id="email-pw"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    disabled={passwordForm.formState.isSubmitting}
                    {...passwordForm.register("email")}
                  />
                </Field>
                <Field
                  id="password"
                  label="Password"
                  error={passwordForm.formState.errors.password?.message}
                >
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    disabled={passwordForm.formState.isSubmitting}
                    {...passwordForm.register("password")}
                  />
                </Field>
                {globalError ? (
                  <p role="alert" className="text-xs text-red-300">
                    {globalError}
                  </p>
                ) : null}
                <Button
                  type="submit"
                  disabled={passwordForm.formState.isSubmitting}
                  className="mt-2"
                >
                  <KeyRound size={16} />
                  {passwordForm.formState.isSubmitting ? "Signing in…" : "Sign in"}
                </Button>
                <button
                  type="button"
                  className="text-xs text-ink-dim hover:text-ink underline-offset-2 hover:underline"
                  onClick={() => {
                    setGlobalError(null);
                    setMode("magic");
                  }}
                >
                  Use a magic link instead
                </button>
                <p className="text-xs text-ink-dim text-center">
                  Access is by invitation. No public signups.
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
